/**
 * scenario-start-ux (Phase 4) 内部型定義。
 *
 * ScenarioIntroOverlay 本体 + stage sub-components + useScenarioStartStage hook が
 * 共通で利用する型を集約する。Scenario / Character は OpenAPI schema から re-export。
 */

import type { components } from "../../api/schema";

/**
 * OpenAPI schema の Scenario。注意: `worldview` / `initialPrompt` は server response には
 * 含まれるが現行 OpenAPI schema が expose しない JSON payload。WorldInfoPanel と同パターンで
 * `extractWorldview` 経由で loose に抽出する。
 */
export type Scenario = components["schemas"]["Scenario"];
export type Character = components["schemas"]["Character"];

/**
 * worldview の known fields (setting / tone / premise) を loose に受ける型。
 * Server が worldview JSON に任意 shape を置けるため unknown で拡張性担保。
 */
export type WorldviewLike = {
  setting?: unknown;
  tone?: unknown;
  premise?: unknown;
  [key: string]: unknown;
};

/**
 * Scenario response から worldview を安全に抽出する helper。
 * OpenAPI schema に worldview が無いため `as unknown` 経由で取り出す。
 */
export function extractWorldview(
  scenario: Scenario | undefined,
): WorldviewLike | null {
  if (scenario === undefined) return null;
  const raw = (scenario as unknown as { worldview?: unknown }).worldview;
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "object") return null;
  return raw as WorldviewLike;
}

/**
 * 導入演出の段階。overlay は title → worldview → closing の順に進む。
 * 登場人物は開始時に明かさず、物語本編とキャラ図鑑で段階開示する。
 * closing は最終段で無限ループ (長引き時も同じ stage に留まる)。
 */
export type StageId = "title" | "worldview" | "closing";

/**
 * Overlay が扱う 2 つのフェーズ。`creating-playline` は POST /playlines 応答前、
 * `fetching-scene` は Playline 作成後 初回 scene 取得中。どちらも同じ overlay shape で
 * 連続表示され、プレイヤーに flicker を感じさせない。
 */
export type ScenarioIntroPhase = "creating-playline" | "fetching-scene";

/**
 * useScenarioStartStage hook の option。
 *
 * - `scenarioReady`: scenario (title / worldview) が取得できていれば true。
 *   false の間 stage は "title" に固定される。
 * - `stageDurations`: 各 stage の持続時間 (ms) の override。未指定は設計値
 *   (title→worldview: 1000 / worldview→closing: 1500)。
 *   テストで短縮するために使用。
 */
export type UseScenarioStartStageOptions = {
  scenarioReady: boolean;
  stageDurations?: Partial<Record<Exclude<StageId, "title">, number>>;
};

/**
 * useScenarioStartStage hook の return。
 *
 * - `stage`: 現在の stage。
 * - `reducedMotion`: `prefers-reduced-motion: reduce` 検出結果。true のとき stage は
 *   即時 "closing" になり、transition/animation が無効化される。
 */
export type UseScenarioStartStageResult = {
  stage: StageId;
  reducedMotion: boolean;
};

/**
 * ScenarioIntroOverlay 本体 props。
 *
 * - `isPending`: true の間 overlay を表示、false 遷移で 300ms fade out。
 * - `phase`: 2 フェーズの現在位置。UI には直接出さず、将来の分岐用。
 * - `scenario`: useScenario の取得結果。undefined のとき fallback 表示。
 * - `scenarioErrored`: useScenario が error のとき true。overlay 自身は滞留せず、
 *   親の ErrorBanner に委任する (Req 5.3)。
 * - `noticeDelayMs`: 長引き補助文言の遅延 (env `longGenerationNoticeMs` を踏襲)。
 * - `stageDurations`: テスト用 hook option の pass-through。
 */
export type ScenarioIntroOverlayProps = {
  isPending: boolean;
  phase: ScenarioIntroPhase;
  scenario?: Scenario | undefined;
  scenarioErrored?: boolean | undefined;
  noticeDelayMs?: number | undefined;
  stageDurations?: UseScenarioStartStageOptions["stageDurations"] | undefined;
};

/**
 * 各 stage の presentation 子 component 共通 props。
 */
export type StageProps = {
  scenario: Scenario;
  reducedMotion: boolean;
};

/**
 * ClosingStage 固有: 長引き補助文言の表示フラグ。
 */
export type ClosingStageProps = StageProps & {
  longRunning: boolean;
};
